import { useMutation } from "@tanstack/react-query";
import { defineMessages, useIntl } from "react-intl";

import { QueryKeys, queryClient } from "@/api/core";
import { TimelineQueryKeys } from "@/api/timelines/query-keys";
import { useApi } from "@/hooks";
import toast from "@/toast";
import { removeAlertsRelatedToAccount } from "@/utils/alert";
import {
  prependPageItem,
  removeAccountStatusesFromTimeline,
  removePageItem,
} from "@/utils/queries";

import type { Account, Relationship } from "@/schemas";

interface Params {
  account: Account;
}

const messages = defineMessages({
  blockSuccess: {
    id: "accounts.block.success",
    defaultMessage: "You have successfully blocked @{username}.",
  },
});

function useBlockAccount() {
  const api = useApi();
  const intl = useIntl();

  return useMutation({
    mutationFn: (params: Params) =>
      api.post<Relationship>(`/api/v1/accounts/${params.account.id}/block`),
    async onMutate(variables) {
      const queryKey = QueryKeys.relationships.byAccount(variables.account.id);
      const blockedAccountsQueryKey = QueryKeys.accounts.blocks();

      // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries({ queryKey });
      await queryClient.cancelQueries({ queryKey: blockedAccountsQueryKey });

      // Snapshot the previous value
      const prevBlockedAccounts = queryClient.getQueryData(
        blockedAccountsQueryKey,
      );
      const prevRelationship = queryClient.getQueryData(
        queryKey,
      ) as Relationship;
      const nextRelationship = { ...prevRelationship, blocking: true };

      // Optimistically update to the new value
      queryClient.setQueryData(queryKey, nextRelationship);
      prependPageItem(blockedAccountsQueryKey, variables.account);

      return {
        queryKey,
        prevRelationship,
        blockedAccountsQueryKey,
        prevBlockedAccounts,
      };
    },
    onSuccess(response, variables) {
      const queryKey = QueryKeys.relationships.byAccount(variables.account.id);
      queryClient.setQueryData(queryKey, response.data);

      // TODO: Bust cache for Feed, Notifications
      removeAccountStatusesFromTimeline(
        TimelineQueryKeys.all,
        variables.account.id,
      );
      removePageItem(
        QueryKeys.suggestions.global,
        variables.account.id,
        (o: any, n: any) => o.account_id === n,
      );

      // We want to remove all alerts related to this account
      removeAlertsRelatedToAccount(variables.account.id);

      // Render a toast
      toast.success(
        intl.formatMessage(messages.blockSuccess, {
          username: variables.account.acct,
        }),
      );
    },
    onError(error, variables, context) {
      // Return Relationship back to its original state
      if (context) {
        queryClient.setQueryData(context.queryKey, context.prevRelationship);
        queryClient.setQueryData(
          context.blockedAccountsQueryKey,
          context.prevBlockedAccounts,
        );
      }
    },
  });
}

export { useBlockAccount };
